import React, { useEffect, useLayoutEffect, useRef } from "react";
import { Button, Form } from "antd";
import Events from "components/events";
import { Localize } from "components/service";
import StaffWorkerRolesField from "../fields/staff-worker-roles-field";
import {
    executeCheckInSetupSave,
    getCheckInSetupSavePlan,
    getEventRoleIds,
    hasValidCheckInLocation,
    normalizeRoleIds,
} from "../../helpers/staff-check-in-setup-helper";


const StaffManagementCheckInSetupForm = ({
    event,
    onGenerateFirstQr,
    onSavingChange,
    isQrRequestInFlight,
    qrStatus,
    qrLoading = false,
}) => {
    const [ form ] = Form.useForm();
    const submittingRef = useRef(false);
    const mountedRef = useRef(true);
    const activeEventIDRef = useRef(event?.id);
    const persistedEventRef = useRef(event);
    const latitude = Form.useWatch("check_latitude", form);
    const longitude = Form.useWatch("check_longitude", form);
    const radius = Form.useWatch("check_radius", form);
    const watchedRoleIds = Form.useWatch("role_id", form);
    const roleIds = watchedRoleIds === undefined ?
        getEventRoleIds(event) :
        normalizeRoleIds(watchedRoleIds);
    const canSave = hasValidCheckInLocation({
        check_latitude: latitude,
        check_longitude: longitude,
        check_radius: radius,
    }) && roleIds.length > 0;

    const {
        _setEventCreateUpdate,
        loadingMutationEventCreateUpdate: loading,
    } = Events.Hooks.createUpdate(event?.id, false);
    const {
        _setEventWorkerRoleUpdate,
        loadingMutationEventWorkerRoleUpdate: rolesLoading,
    } = Events.Hooks.workerRoleUpdate();

    activeEventIDRef.current = event?.id;

    useEffect(() => {
        if (!submittingRef.current || String(persistedEventRef.current?.id) !== String(event?.id))
        {
            persistedEventRef.current = event;
        }
    }, [ event ]);

    useLayoutEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
        };
    }, []);

    const handleSubmit = async (values) => {
        if (
            !canSave ||
            !hasValidCheckInLocation(values) ||
            normalizeRoleIds(values.role_id).length === 0 ||
            qrLoading ||
            isQrRequestInFlight?.() ||
            submittingRef.current
        )
        {
            return;
        }

        const submitEventID = event.id;
        const isSubmitActive = () => (
            mountedRef.current &&
            String(activeEventIDRef.current) === String(submitEventID)
        );

        try
        {
            submittingRef.current = true;
            onSavingChange?.(true, submitEventID);
            const plan = getCheckInSetupSavePlan({
                event: persistedEventRef.current,
                values,
                qrStatus,
            });

            await executeCheckInSetupSave({
                event: persistedEventRef.current,
                eventID: submitEventID,
                plan,
                isActive: isSubmitActive,
                onPersist: (savedEvent) => {
                    persistedEventRef.current = savedEvent;
                },
                updateLocation: (input) => _setEventCreateUpdate({
                    variables: { input },
                }),
                updateRoles: (roleIds) => _setEventWorkerRoleUpdate({
                    variables: {
                        eeID: String(submitEventID),
                        input: {
                            ee_id: submitEventID,
                            role_id: roleIds,
                        },
                    },
                }),
                generateFirstQr: onGenerateFirstQr,
            });
        } catch (error)
        {
            // Entity mutation hooks present request errors to the user.
        } finally
        {
            submittingRef.current = false;
            onSavingChange?.(false, submitEventID);
        }
    };

    return (
        <Form
            layout="vertical"
            form={ form }
            className="model-form form-controls-compact staff-management-check-in-setup-form"
            onFinish={ handleSubmit }>
            <h2><Localize>STAFF_MANAGEMENT.Section_SetUpCheckIn</Localize></h2>

            <p className="staff-management-check-in-setup-form__step">
                <Localize>STAFF_MANAGEMENT.Step_ChooseGroups</Localize>
            </p>

            <StaffWorkerRolesField event={ event } form={ form } />

            <p className="staff-management-check-in-setup-form__step">
                <Localize>STAFF_MANAGEMENT.Step_SetUpLocation</Localize>
            </p>

            <Events.Fields.LocationMap
                event={ event }
                form={ form }
                bindLocationMetadata={ true }
                markerDraggable={ true }
                initializeDefaultRadius={ false }
                searchUpdatesLocation={ true }
                addressLabel={ <Localize>STAFF_MANAGEMENT.Field_Address</Localize> }
                addressPlaceholder={ Localize({ children: "STAFF_MANAGEMENT.Placeholder_Address" }).props.children }
                mapHint={ <Localize>STAFF_MANAGEMENT.Text_DragPin</Localize> } />

            <div className="form-btn-holder">
                <Button
                    type="primary"
                    htmlType="submit"
                    disabled={ !canSave || qrLoading }
                    loading={ loading || rolesLoading || qrLoading }>
                    <Localize>GLOBAL.Button_Text_Save</Localize>
                </Button>
            </div>
        </Form>
    );
};

export default StaffManagementCheckInSetupForm;
